/**
 * Compressed time scale that skips large gaps.
 * Maps continuous pixel space to discontinuous time domain.
 * Used in single mode to create seamless chart flow without gaps.
 */

import type { DataGap, Scale } from "~types/types";

// ==================== Types ====================

export interface CompressedTimeScaleOptions {
  /** Time domain [start, end] in milliseconds */
  domain: [number, number];
  /** Pixel range [start, end] */
  range: [number, number];
  /** Array of gaps in the data */
  gaps: DataGap[];
}

interface TimeSegment {
  domainStart: number;
  domainEnd: number;
  rangeStart: number;
  rangeEnd: number;
}

// ==================== Scale Creation ====================

/**
 * Create a compressed time scale for single mode.
 * Large gaps are removed from the time domain, creating a continuous visual flow.
 * Points inside gaps snap to the nearest segment edge.
 */
export function createCompressedTimeScale(
  options: CompressedTimeScaleOptions
): Scale {
  const { domain, range, gaps } = options;
  const [domainStart, domainEnd] = domain;
  const [rangeStart, rangeEnd] = range;

  // Filter to only large gaps within the domain
  const largeGaps = gaps
    .filter((g) => g.isLarge)
    .filter((g) => g.end > domainStart && g.start < domainEnd)
    .sort((a, b) => a.start - b.start);

  // If no large gaps, return a simple linear scale
  if (largeGaps.length === 0) {
    const domainRange = domainEnd - domainStart;
    const rangeSpan = rangeEnd - rangeStart;

    return {
      domain: () => domain,
      range: () => range,
      scale: (value: number): number => {
        if (domainRange === 0) return rangeStart;
        const t = (value - domainStart) / domainRange;

        return rangeStart + t * rangeSpan;
      },
      invert: (pixel: number): number => {
        if (rangeSpan === 0) return domainStart;
        const t = (pixel - rangeStart) / rangeSpan;

        return domainStart + t * domainRange;
      },
    };
  }

  // Calculate total time excluding large gaps
  let totalGapTime = 0;

  for (const gap of largeGaps) {
    const gapStart = Math.max(gap.start, domainStart);
    const gapEnd = Math.min(gap.end, domainEnd);

    totalGapTime += gapEnd - gapStart;
  }

  const effectiveTimeRange = domainEnd - domainStart - totalGapTime;
  const rangeSpan = rangeEnd - rangeStart;

  // Build segments between gaps
  const segments: TimeSegment[] = [];
  let currentDomainPos = domainStart;
  let currentRangePos = rangeStart;

  for (const gap of largeGaps) {
    const gapStart = Math.max(gap.start, domainStart);

    if (gapStart > currentDomainPos) {
      const segmentDuration = gapStart - currentDomainPos;
      const segmentPixels =
        effectiveTimeRange > 0
          ? (segmentDuration / effectiveTimeRange) * rangeSpan
          : 0;

      segments.push({
        domainStart: currentDomainPos,
        domainEnd: gapStart,
        rangeStart: currentRangePos,
        rangeEnd: currentRangePos + segmentPixels,
      });

      currentRangePos += segmentPixels;
    }

    currentDomainPos = Math.min(gap.end, domainEnd);
  }

  // Final segment after last gap
  if (currentDomainPos < domainEnd) {
    const segmentDuration = domainEnd - currentDomainPos;
    const segmentPixels =
      effectiveTimeRange > 0
        ? (segmentDuration / effectiveTimeRange) * rangeSpan
        : 0;

    segments.push({
      domainStart: currentDomainPos,
      domainEnd,
      rangeStart: currentRangePos,
      rangeEnd: currentRangePos + segmentPixels,
    });
  }

  // Handle edge case: no segments created
  if (segments.length === 0) {
    return {
      domain: () => domain,
      range: () => range,
      scale: () => rangeStart,
      invert: () => domainStart,
    };
  }

  function scaleWithinSegment(timestamp: number): number | undefined {
    for (const segment of segments) {
      if (timestamp >= segment.domainStart && timestamp <= segment.domainEnd) {
        const span = segment.domainEnd - segment.domainStart;

        if (span === 0) return segment.rangeStart;

        const t = (timestamp - segment.domainStart) / span;

        return segment.rangeStart + t * (segment.rangeEnd - segment.rangeStart);
      }
    }

    return undefined;
  }

  function snapToGapEdge(timestamp: number): number | undefined {
    for (let i = 0; i < segments.length - 1; i++) {
      const current = segments[i];
      const next = segments[i + 1];

      if (!current || !next) continue;
      if (timestamp > current.domainEnd && timestamp < next.domainStart) {
        return current.rangeEnd;
      }
    }

    return undefined;
  }

  function scaleTimestamp(timestamp: number): number {
    if (timestamp <= domainStart) return rangeStart;
    if (timestamp >= domainEnd) return rangeEnd;

    const segmentResult = scaleWithinSegment(timestamp);

    if (segmentResult !== undefined) return segmentResult;

    const gapResult = snapToGapEdge(timestamp);

    if (gapResult !== undefined) return gapResult;

    const first = segments[0];
    const last = segments.at(-1);

    if (first && timestamp < first.domainStart) return first.rangeStart;
    if (last && timestamp > last.domainEnd) return last.rangeEnd;

    return rangeStart;
  }

  function invertPixel(pixel: number): number {
    if (pixel <= rangeStart) return domainStart;
    if (pixel >= rangeEnd) return domainEnd;

    for (const segment of segments) {
      if (pixel >= segment.rangeStart && pixel <= segment.rangeEnd) {
        const span = segment.rangeEnd - segment.rangeStart;

        if (span === 0) return segment.domainStart;

        const t = (pixel - segment.rangeStart) / span;

        return (
          segment.domainStart + t * (segment.domainEnd - segment.domainStart)
        );
      }
    }

    // Between segments — return start of next segment
    for (let i = 0; i < segments.length - 1; i++) {
      const current = segments[i];
      const next = segments[i + 1];

      if (!current || !next) continue;
      if (pixel > current.rangeEnd && pixel < next.rangeStart) {
        return next.domainStart;
      }
    }

    return domainStart;
  }

  return {
    domain: () => domain,
    range: () => range,
    scale: scaleTimestamp,
    invert: invertPixel,
  };
}

/**
 * Get the effective timestamps visible in the scale (excluding gaps).
 * Useful for generating axis ticks that don't fall in gaps.
 */
export function getVisibleTimestampsFromScale(
  scale: Scale,
  gaps: DataGap[],
  tickCount: number
): number[] {
  if (tickCount <= 0) return [];
  if (tickCount === 1) return [scale.domain()[0]];

  const [domainStart, domainEnd] = scale.domain();
  const largeGaps = gaps.filter((g) => g.isLarge);

  if (largeGaps.length === 0) {
    // Simple linear distribution
    const step = (domainEnd - domainStart) / (tickCount - 1);
    const ticks: number[] = [];

    for (let i = 0; i < tickCount; i++) {
      ticks.push(domainStart + i * step);
    }

    return ticks;
  }

  // Generate ticks evenly spaced in pixel space, then convert to time
  const [rangeStart, rangeEnd] = scale.range();
  const step = (rangeEnd - rangeStart) / (tickCount - 1);
  const ticks: number[] = [];

  for (let i = 0; i < tickCount; i++) {
    const pixel = rangeStart + i * step;

    ticks.push(scale.invert(pixel));
  }

  return ticks;
}
